// Job level groups — the only level grain shown to users.
//
// Models, staff plans and the engine keep exact L1–L8 levels (rates and
// headcount stay precise); every view and picker works in these four groups.

import { LEVEL_IDS } from '../types.js';
import type { LevelId } from '../types.js';

export type LevelGroupId = 'junior' | 'mid' | 'manager' | 'executive';

export interface LevelGroup {
  id: LevelGroupId;
  /** Short name, e.g. 'Mid-Level'. */
  name: string;
  /** Display label with range, e.g. 'Mid-Level (L3-4)'. */
  label: string;
  levelIds: readonly LevelId[];
}

export const LEVEL_GROUPS: readonly LevelGroup[] = [
  { id: 'junior', name: 'Junior', label: 'Junior (L1-2)', levelIds: ['L1', 'L2'] },
  { id: 'mid', name: 'Mid-Level', label: 'Mid-Level (L3-4)', levelIds: ['L3', 'L4'] },
  { id: 'manager', name: 'Manager', label: 'Manager (L5-6)', levelIds: ['L5', 'L6'] },
  { id: 'executive', name: 'Executive', label: 'Executive (L7-8)', levelIds: ['L7', 'L8'] },
];

const GROUP_BY_LEVEL = new Map<LevelId, LevelGroup>(
  LEVEL_GROUPS.flatMap((g) => g.levelIds.map((l) => [l, g] as const)),
);

export function levelGroupOf(levelId: LevelId): LevelGroup {
  const g = GROUP_BY_LEVEL.get(levelId);
  if (!g) throw new Error(`Unknown level ${levelId}`);
  return g;
}

export function levelGroupById(id: LevelGroupId): LevelGroup {
  const g = LEVEL_GROUPS.find((x) => x.id === id);
  if (!g) throw new Error(`Unknown level group ${id}`);
  return g;
}

/** Exact-level label for pickers that still store L#, e.g. 'L3 · Mid-Level'. */
export function exactLevelLabel(levelId: LevelId): string {
  const g = GROUP_BY_LEVEL.get(levelId);
  return g ? `${levelId} · ${g.name}` : levelId;
}

/** Groups touched by any of `levelIds`, in group order. */
export function groupsForLevels(levelIds: readonly LevelId[] | null | undefined): LevelGroupId[] {
  const set = new Set(levelIds || []);
  return LEVEL_GROUPS.filter((g) => g.levelIds.some((l) => set.has(l))).map((g) => g.id);
}

/** Every exact level in the given groups, in L1–L8 order. */
export function levelsForGroups(groupIds: readonly LevelGroupId[]): LevelId[] {
  const set = new Set(groupIds);
  return LEVEL_IDS.filter((l) => set.has(levelGroupOf(l).id));
}

/** Sum a per-level map into per-group totals (headcount, FTE, hours, cost). */
export function sumByLevelGroup(
  byLevel: Partial<Record<LevelId, number>> | undefined,
): Record<LevelGroupId, number> {
  const out = { junior: 0, mid: 0, manager: 0, executive: 0 } as Record<LevelGroupId, number>;
  for (const [l, v] of Object.entries(byLevel || {}) as [LevelId, number][]) {
    const g = GROUP_BY_LEVEL.get(l);
    if (g) out[g.id] += Number(v) || 0;
  }
  return out;
}

/** Average of a group's levels that have a value (rates, percentages, shares). */
export function averageForLevelGroup(
  group: LevelGroup,
  valueFor: (levelId: LevelId) => number | undefined,
): number | undefined {
  const vals = group.levelIds
    .map(valueFor)
    .filter((v): v is number => v !== undefined && Number.isFinite(v));
  if (vals.length === 0) return undefined;
  return vals.reduce((s, v) => s + v, 0) / vals.length;
}

/**
 * New per-level values when a group-level figure is edited. Each level keeps
 * its share of the group (`current` values are scaled together), so the L1 vs
 * L2 difference survives an edit. With nothing to scale (all zero / unset),
 * every level takes `target` (averages) or an even split (sums). For
 * averages, an unset level is skipped like in `averageForLevelGroup` and is
 * filled with `target`.
 */
export function scaleLevelGroup(
  group: LevelGroup,
  current: (levelId: LevelId) => number | undefined,
  target: number,
  mode: 'average' | 'sum',
): Partial<Record<LevelId, number>> {
  const ids = group.levelIds;
  const raw = ids.map((l) => {
    const v = current(l);
    return v !== undefined && Number.isFinite(v) ? v : undefined;
  });
  const defined = raw.filter((v): v is number => v !== undefined);
  const now =
    mode === 'sum'
      ? defined.reduce((s, v) => s + v, 0)
      : defined.length
        ? defined.reduce((s, v) => s + v, 0) / defined.length
        : 0;
  const out: Partial<Record<LevelId, number>> = {};
  ids.forEach((l, i) => {
    const v = raw[i];
    if (Math.abs(now) <= 1e-12) out[l] = mode === 'sum' ? target / ids.length : target;
    else if (v === undefined) out[l] = mode === 'sum' ? 0 : target;
    else out[l] = v * (target / now);
  });
  return out;
}
